import { useEffect } from 'react';

import Tracker from '~/src/common/services/Tracker';
import { getCommonProductsEventProperties } from '~/src/common/utils/product';
import { useSearchItemsInfinite } from '~/src/queries/api-ecom/generated/api-ecom';
import { SearchItemsModelVersion } from '~/src/queries/api-ecom/generated/api-ecom.schemas';
import { isProduct } from '~/src/typings/products/guards';

import { BACKEND_TO_AMPLITUDE } from '../constants';
import { Source, TabName, Type } from '../types';

import { useSearchParams } from './useSearchParams';

/** Recherche pour un type de résultats en particulier */
export const useSearchResultsType = (
  type: Type,
  params: {
    text: string;
    source?: Source;
    tabName?: TabName;
    modelVersion?: SearchItemsModelVersion;
  },
) => {
  const { text, source, tabName, modelVersion } = params;
  const { searchID } = useSearchParams();
  // Récupération des données correspondant au type, via une liste infinie
  const results = useSearchItemsInfinite(
    { type, text, modelVersion },
    { query: { getNextPageParam: page => page.next, staleTime: Infinity } },
  );

  // Liste des elements
  const { data } = results;
  const count = data?.pages[0]?.count;
  const items = data?.pages.flatMap(p => p.items);

  // Booleen utilitaires
  const { isError, isFetching, isFetched } = results;
  const isLoaded = (isFetched && !!data) || isError;

  // Gestion du chargement infini
  const { hasNextPage, fetchNextPage } = results;
  const loadMore = () => void fetchNextPage();
  const hasMore = hasNextPage === true;

  // Amplitude
  useEffect(() => {
    // Derniere page et son index
    const index = data?.pages.length;
    const lastPage = index ? data.pages[index - 1] : null;
    const resultsType = BACKEND_TO_AMPLITUDE[type];
    // Envoi si sur bon onglet, et si toutes les données sont présentes
    if (resultsType !== tabName || index == null || lastPage == null) return;

    Tracker.sendEvent('view search results', {
      'is page viewed': true,
      'keywords': text,
      'search type': source,
      'results type': resultsType,
      'number of search results': lastPage.count,
      'algorithm': lastPage.algoVersion,
      'page index': index,
      ...(searchID ? { 'search ID': searchID } : null),
      ...(type === 'PRODUCT' && getCommonProductsEventProperties(lastPage.items.filter(isProduct))),
    });
  }, [data, text, type, source, tabName, searchID]);

  return { items, count, isError, isLoaded, isFetching, hasMore, loadMore };
};
